import { useState, useEffect, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../api';
import { useAuth } from '../context/AuthContext';
import ToolbarFilterDropdown from '../components/ToolbarFilterDropdown';
import './Customers.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export default function Customers() {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [countryFilter, setCountryFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pageSizeOpen, setPageSizeOpen] = useState(false);
  const pageSizeDdRef = useRef(null);
  const [filterMenuOpen, setFilterMenuOpen] = useState(null);

  useEffect(() => {
    if (!pageSizeOpen) return undefined;
    const close = () => setPageSizeOpen(false);
    const onDoc = (e) => {
      if (pageSizeDdRef.current && !pageSizeDdRef.current.contains(e.target)) close();
    };
    const onKey = (e) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [pageSizeOpen]);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    api.get('/customers')
      .then((rows) => mounted && setCustomers(rows || []))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, []);

  const countries = [...new Set(customers.map(c => c.country).filter(Boolean))].sort();

  const statusFilterOptions = useMemo(
    () => [
      { value: '', label: 'All Statuses' },
      { value: 'Active', label: 'Active' },
      { value: 'Inactive', label: 'Inactive' },
      { value: 'Suspended', label: 'Suspended' },
    ],
    []
  );

  const riskFilterOptions = useMemo(
    () => [
      { value: '', label: 'All Risk' },
      { value: 'Low', label: 'Low' },
      { value: 'Medium', label: 'Medium' },
      { value: 'High', label: 'High' },
    ],
    []
  );

  const countryFilterOptions = useMemo(
    () => [{ value: '', label: 'All Countries' }, ...countries.map(c => ({ value: c, label: c }))],
    [countries]
  );

  const filtered = useMemo(() => customers.filter(c => {
    if (statusFilter && c.status !== statusFilter) return false;
    if (riskFilter && c.risk_category !== riskFilter) return false;
    if (countryFilter && c.country !== countryFilter) return false;
    if (!search) return true;
    const q = search.toLowerCase();
    return [c.customer_id, c.legal_name, c.trade_name, c.primary_contact, c.contact_email, c.default_currency]
      .some(v => (v || '').toLowerCase().includes(q));
  }), [customers, search, statusFilter, riskFilter, countryFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter, riskFilter, countryFilter, pageSize]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filtered.length / pageSize));
    setPage(p => Math.min(p, tp));
  }, [filtered.length, pageSize]);

  const totalFiltered = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / pageSize));
  const safePage = Math.min(Math.max(1, page), totalPages);
  const offset = (safePage - 1) * pageSize;
  const pageRows = filtered.slice(offset, offset + pageSize);
  const rangeStart = totalFiltered === 0 ? 0 : offset + 1;
  const rangeEnd = totalFiltered === 0 ? 0 : Math.min(offset + pageSize, totalFiltered);

  return (
    <>
      <div className="page-header">
        <h1>Customer Master</h1>
      </div>
      <div className="page-content customers-page">
        {(() => {
          const hasFilters = !!(search || statusFilter || riskFilter || countryFilter);
          const clearFilters = () => {
            setSearch('');
            setStatusFilter('');
            setRiskFilter('');
            setCountryFilter('');
          };

          return (
            <>
              <div className="customers-top">
                <div className="customers-stats" aria-label="Customer counts">
                  <div className="customers-stat">
                    <div className="customers-stat-label">In list</div>
                    <div className="customers-stat-value">{loading ? '—' : customers.length.toLocaleString()}</div>
                  </div>
                  <div className="customers-stat">
                    <div className="customers-stat-label">Match filters</div>
                    <div className="customers-stat-value">{loading ? '—' : filtered.length.toLocaleString()}</div>
                  </div>
                </div>
                <div className="customers-top-right">
                  {hasFilters && (
                    <button className="btn btn-outline btn-sm" onClick={clearFilters}>Clear filters</button>
                  )}
                  <Link to="/customers/new" className="btn btn-primary btn-sm customers-new-btn">+ New Customer</Link>
                </div>
              </div>

              {user?.role !== 'Admin' && (
                <p className="customers-scope-note">
                  Customer rows reflect <strong>data access</strong> assigned to your user (User Management → Data Access). Non-admin users do not see the full master list unless every customer is assigned.
                </p>
              )}

              {customers.length > 0 && filtered.length < customers.length && (
                <div className="customers-filter-hint" role="status">
                  <span>
                    Filters narrow the table to <strong>{filtered.length}</strong> of <strong>{customers.length}</strong> customer{customers.length === 1 ? '' : 's'} in your current list.
                  </span>
                  <button type="button" className="btn btn-outline btn-sm" onClick={clearFilters}>
                    Clear filters
                  </button>
                </div>
              )}

              <div className="customers-toolbar">
                <div className="customers-toolbar-left">
                  <div className="customers-search">
                    <span className="customers-search-icon" aria-hidden="true">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                        <path d="M10.5 18a7.5 7.5 0 1 1 0-15 7.5 7.5 0 0 1 0 15Z" stroke="currentColor" strokeWidth="2"/>
                        <path d="M16.3 16.3 21 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                      </svg>
                    </span>
                    <input
                      value={search}
                      onChange={e => setSearch(e.target.value)}
                      placeholder="Search by ID, name, contact, currency…"
                      aria-label="Search customers"
                    />
                  </div>

                  <div className="customers-filters">
                    <ToolbarFilterDropdown
                      id="cust-filter-status"
                      labelText="Status"
                      ariaLabel="Filter by status"
                      value={statusFilter}
                      onChange={setStatusFilter}
                      placeholder="All Statuses"
                      options={statusFilterOptions}
                      menuKey="cust-status"
                      openMenu={filterMenuOpen}
                      setOpenMenu={setFilterMenuOpen}
                    />
                    <ToolbarFilterDropdown
                      id="cust-filter-risk"
                      labelText="Risk"
                      ariaLabel="Filter by risk"
                      value={riskFilter}
                      onChange={setRiskFilter}
                      placeholder="All Risk"
                      options={riskFilterOptions}
                      menuKey="cust-risk"
                      openMenu={filterMenuOpen}
                      setOpenMenu={setFilterMenuOpen}
                    />
                    <ToolbarFilterDropdown
                      id="cust-filter-country"
                      labelText="Country"
                      ariaLabel="Filter by country"
                      value={countryFilter}
                      onChange={setCountryFilter}
                      placeholder="All Countries"
                      options={countryFilterOptions}
                      menuKey="cust-country"
                      openMenu={filterMenuOpen}
                      setOpenMenu={setFilterMenuOpen}
                      wide
                    />
                  </div>
                </div>
              </div>
            </>
          );
        })()}
        <div className="card">
          <div className="customers-table">
            <table>
              <thead>
                <tr>
                  <th>ID</th><th>Legal Name</th><th>Trade Name</th><th>Country</th>
                  <th>Currency</th><th>Contact</th><th>Risk</th><th>Status</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map(c => (
                  <tr key={c.customer_id}>
                    <td><Link to={`/customers/${c.customer_id}`} className="link">{c.customer_id}</Link></td>
                    <td>{c.legal_name}</td>
                    <td>{c.trade_name}</td>
                    <td>{c.country}</td>
                    <td>{c.default_currency}</td>
                    <td>{c.primary_contact}</td>
                    <td><span className={`badge badge-${c.risk_category === 'High' ? 'expired' : c.risk_category === 'Medium' ? 'draft' : 'active'}`}>{c.risk_category}</span></td>
                    <td><span className={`badge badge-${c.status?.toLowerCase()}`}>{c.status}</span></td>
                  </tr>
                ))}
                {!filtered.length && (
                  <tr>
                    <td colSpan="8" className="customers-empty">
                      {loading ? 'Loading customers…' : 'No customers match the filters.'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {!loading && totalFiltered > 0 && (
            <div className="customers-pagination" role="navigation" aria-label="Table pagination">
              <div className="customers-pagination-summary">
                <span className="customers-pagination-summary-label">Showing</span>
                <span className="customers-pagination-summary-range">
                  <strong className="tabular-nums">{rangeStart}</strong>
                  <span className="customers-pagination-summary-dash">–</span>
                  <strong className="tabular-nums">{rangeEnd}</strong>
                  <span className="customers-pagination-summary-of">of</span>
                  <strong className="tabular-nums">{totalFiltered.toLocaleString()}</strong>
                  <span className="customers-pagination-summary-unit">rows</span>
                </span>
              </div>
              <div className="customers-pagination-controls">
                <div className="customers-pagination-page-size" ref={pageSizeDdRef}>
                  <span className="customers-pagination-label" id="customers-page-size-label">Rows per page</span>
                  <div className="customers-page-size-dd-inner">
                    <button
                      type="button"
                      className={`customers-page-size-trigger ${pageSizeOpen ? 'customers-page-size-trigger-open' : ''}`}
                      aria-haspopup="listbox"
                      aria-expanded={pageSizeOpen}
                      aria-label={`Rows per page, current value ${pageSize}`}
                      onClick={() => setPageSizeOpen(o => !o)}
                    >
                      <span>{pageSize}</span>
                      <span className="customers-page-size-chevron" aria-hidden>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </span>
                    </button>
                    {pageSizeOpen && (
                      <ul
                        className="customers-page-size-menu"
                        role="listbox"
                        aria-labelledby="customers-page-size-label"
                      >
                        {PAGE_SIZE_OPTIONS.map(n => (
                          <li
                            key={n}
                            role="option"
                            aria-selected={pageSize === n}
                            className={
                              pageSize === n
                                ? 'customers-page-size-option customers-page-size-option-active'
                                : 'customers-page-size-option'
                            }
                            onMouseDown={(e) => {
                              e.preventDefault();
                              setPageSize(n);
                              setPageSizeOpen(false);
                            }}
                          >
                            {n}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
                <div className="customers-pagination-pager" role="group" aria-label="Page navigation">
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(1)}
                    aria-label="First page"
                    title="First page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M11 19l-7-7 7-7" />
                        <path d="M18 19l-7-7 7-7" />
                      </svg>
                    </span>
                    <span>First</span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage <= 1}
                    onClick={() => setPage(p => Math.max(1, p - 1))}
                    aria-label="Previous page"
                    title="Previous page"
                  >
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M15 18l-6-6 6-6" />
                      </svg>
                    </span>
                    <span>Prev</span>
                  </button>
                  <div className="customers-pagination-page-chip">
                    <span className="customers-pagination-page-label">Page</span>
                    <span className="customers-pagination-page-current tabular-nums">{safePage}</span>
                    <span className="customers-pagination-page-sep">/</span>
                    <span className="customers-pagination-page-total tabular-nums">{totalPages}</span>
                  </div>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                    aria-label="Next page"
                    title="Next page"
                  >
                    <span>Next</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 18l6-6-6-6" />
                      </svg>
                    </span>
                  </button>
                  <button
                    type="button"
                    className="customers-pagination-step"
                    disabled={safePage >= totalPages}
                    onClick={() => setPage(totalPages)}
                    aria-label="Last page"
                    title="Last page"
                  >
                    <span>Last</span>
                    <span className="customers-pagination-step-icon" aria-hidden>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M13 5l7 7-7 7" />
                        <path d="M6 5l7 7-7 7" />
                      </svg>
                    </span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
